/**
 * Capabilities:
 * - CAP-REALTIME-STAFF-UPDATES
 *
 * Responsibilities:
 * - Maintain the authenticated browser WebSocket connection to the backend.
 * - Correlate request/reply-style socket calls and fan out push events to
 *   registered frontend handlers.
 *
 * Context:
 * - project-intelligence/capabilities/requested/staff/realtime-updates.md
 * - project-intelligence/architecture/frontend-applications.md
 * - project-intelligence/architecture/authentication-and-authorization.md
 */

import _ from 'underscore';

import ReconnectingWebSocket from 'reconnecting-websocket';
import { Auth } from 'aws-amplify';

import { isDev, doIfDev, exposeIfDev } from './general-utils';

let wsEndpoint = null;

let _handerIdx = 10000000;
const getNextHandlerIdx = () => {
    _handerIdx += 1;
    return _handerIdx;
}

let _msgIdx = 10000000;
const getNextMsgIdx = () => {
    _msgIdx += 1;
    return _msgIdx;
}

const wsMessageHandlers = {};
exposeIfDev({
    eventMessageHandlers: wsMessageHandlers
});

function addMessageHandler(handlerName, handler, { errorHandler, condition } = {}) {
    const handlerIdx = getNextHandlerIdx();
    const handlerId = `${handlerName}|${handlerIdx}`;
    if (isDev) {
        console.info(`[WS|${(new Date()).toISOString()}] Registering WS Message handler: ${handlerId}`)
    }
    wsMessageHandlers[handlerId] = { handlerName, handler, errorHandler, condition };
    return handlerId;
}

function addMessageHandlerForEvent(eventName, hander, errorHandler) {
    /**
     * CAP-REALTIME-STAFF-UPDATES: bind a handler to the backend's `eventName`
     * convention so feature modules can subscribe without reimplementing the
     * payload filter each time.
     */
    return addMessageHandler(eventName, hander, {
        errorHandler,
        condition: payload => payload.eventName === eventName,
    });      
}

function removeMessageHandler(handlerId) {
    if (!!wsMessageHandlers[handlerId]) {
        delete wsMessageHandlers[handlerId];
    }
}

const ws = new ReconnectingWebSocket(async () => {
    const user = await Auth.currentAuthenticatedUser();
    const idToken = user.signInUserSession.idToken.jwtToken
    const url = `${wsEndpoint}?Authorizer=${idToken}`;
    console.info(`[WS|${(new Date()).toISOString()}] Connecting to ${url}`)
    return url;
}, [], {
    maxReconnectionDelay: 10000,
    minReconnectionDelay: 2000,
    reconnectionDelayGrowFactor: 1.5,
    startClosed: true,
    debug: true,
});

function setEndpoint(url, connectNow = true) {
    /**
     * Frontend bootstrap contract: allow index.js to swap in a deployed custom
     * domain while preserving the reconnecting socket instance.
     */
    console.info(`[WS|${(new Date()).toISOString()}] Setting WS API Endpoint Url: ${url}`)
    wsEndpoint = url;
    if (connectNow) {
        console.info(`[WS|${(new Date()).toISOString()}] Reconnecting...`)
        ws.reconnect();
    }
}

const activeWSCalls = {}
exposeIfDev({
    activeWSCalls
});



function send(data, { isCall = true, callTimeout = 60 * 1000 } = {}) {
    /**
     * CAP-REALTIME-STAFF-UPDATES: send a socket message and, by default,
     * allocate a correlation id so the caller receives a Promise that resolves
     * or rejects from the matching backend response event.
     */
    if (ws.readyState !== ws.OPEN) {
        throw new Error('ws-not-ready');
    }
    const msgIdx = getNextMsgIdx();
    if (isCall && _.isObject(data)) {
        data = _.extend({ __msg_idx__: msgIdx }, data);
    }
    ws.send(JSON.stringify(data))

    if (isCall) {
        const callRecord = { msgIdx, complete: false };
        activeWSCalls[msgIdx] = callRecord;
        callRecord.promise = new Promise((resolve, reject) => {
            callRecord.resolve = data => {
                if (!callRecord.complete) {
                    callRecord.complete = true;
                    if (!!activeWSCalls[msgIdx]) {
                        delete activeWSCalls[msgIdx];
                    }
                    resolve(data);
                }
            }
            callRecord.reject = error => {
                if (!callRecord.complete) {
                    callRecord.complete = true;
                    if (!!activeWSCalls[msgIdx]) {
                        delete activeWSCalls[msgIdx];
                    }
                    reject(error);
                }
            }
        });
    
        setTimeout(() => {
            if (!!activeWSCalls[msgIdx]) {
                if (!callRecord.complete) {
                    callRecord.reject({
                        eventName: 'error',
                        errorType: 'call-timeout',
                        context: data,
                    })
                }
                delete activeWSCalls[msgIdx];
            }
        }, callTimeout);
    
        return callRecord.promise;
    }
}

addMessageHandler('resolve-calls', payload => {
    const msgIdx = payload.context.__msg_idx__;
    const callRecord = activeWSCalls[msgIdx];
    if (payload.eventName === 'error') {
        console.error(`[WS|${(new Date()).toISOString()}|${msgIdx}] Call Rejected:`, payload)
        callRecord.reject(payload);
    } else {
        console.info(`[WS|${(new Date()).toISOString()}|${msgIdx}] Call Resolved:`, payload)
        callRecord.resolve(payload);
    }
}, {
    condition: payload => {
        return payload && payload.context && !!payload.context.__msg_idx__ && !!activeWSCalls[payload.context.__msg_idx__];
    }
})

const wsConnectHandlers = [];
const wsDisconnectHandlers = [];
exposeIfDev({
    wsConnectHandlers, wsDisconnectHandlers
});

function addConnectHandler(handlerName, handler, errorHandler) {
    if (isDev) {
        console.info(`[WS|${(new Date()).toISOString()}] Registering WS Connect handler: ${handlerName}`)
    }
    wsConnectHandlers.push({ handlerName, handler, errorHandler });
}

function addDisconnectHandler(handlerName, handler, errorHandler) {
    if (isDev) {
        console.info(`[WS|${(new Date()).toISOString()}] Registering WS Disconnect handler: ${handlerName}`)
    }
    wsDisconnectHandlers.push( { handlerName, handler, errorHandler });
}


ws.addEventListener('open', event => {
    console.info(`[WS|${(new Date()).toISOString()}] Connected`, event)

    wsConnectHandlers
        .forEach(({ handlerName, handler, errorHandler = () => null }) => {
            try {
                console.info(`[WS|${(new Date()).toISOString()}] Running WS connect handler ${handlerName}...`)
                handler(event);
            } catch (e) {
                try {
                    console.error(`[WS|${(new Date()).toISOString()}] Error in WS connect handler ${handlerName}:`, e, event)
                    errorHandler(e, event);
                } catch {
                    // do nothing
                }
            }
        });
});

function handleDisconnectOrError(event) {
    wsDisconnectHandlers
        .forEach(({ handlerName, handler, errorHandler = () => null }) => {
            try {
                console.info(`[WS|${(new Date()).toISOString()}] Running WS disconnect handler ${handlerName}...`)
                handler(event);
            } catch (e) {
                try {
                    console.error(`[WS|${(new Date()).toISOString()}] Error in WS disconnect handler ${handlerName}:`, e, event)
                    errorHandler(e, event);
                } catch {
                    // do nothing
                }
            }
        });
}

ws.addEventListener('close', event => {
    console.info(`[WS|${(new Date()).toISOString()}] Disconnected`, event)
    handleDisconnectOrError(event);
});

ws.addEventListener('error', event => {
    console.error(`[WS|${(new Date()).toISOString()}] Disconnected (Error)`, event)
    handleDisconnectOrError(event);
});

ws.addEventListener('message', event => {
    let payload = null;
    try {
        payload = JSON.parse(event.data);
        if (!!payload.eventName) {
            console.info(`[WS|${(new Date()).toISOString()}] Received Event (${payload.eventName})\nData:`, payload)
        } else {
            console.info(`[WS|${(new Date()).toISOString()}] Received Payload:`, payload)
        }
    } catch {
        console.info(`[WS|${(new Date()).toISOString()}] Message:`, event)
    }

    Object.keys(wsMessageHandlers)
        .map(k => wsMessageHandlers[k])
        .forEach(({ handlerName, handler, errorHandler = () => null, condition = () => true }) => {
            if (!condition(payload, event)) {
                return;
            }

            try {
                console.info(`[WS|${(new Date()).toISOString()}] Running WS handler ${handlerName}...`)
                handler(payload, event);
            } catch (e) {
                try {
                    console.error(`[WS|${(new Date()).toISOString()}] Error in WS message handler ${handlerName}:`, e, payload, event)
                    errorHandler(e, payload, event);
                } catch {
                    // do nothing
                }
            }
        });
});


const exports = {
    setEndpoint,
    send,
    connect: () => {
        if (ws.readyState !== ws.CONNECTING && ws.readyState !== ws.OPEN) {
            ws.reconnect();
        }
    },
    reconnect: () => ws.reconnect(),
    disconnect: () => {
        if (ws.readyState === ws.OPEN) {
            ws.close();
        }
    },
    addMessageHandler,
    addMessageHandlerForEvent,
    removeMessageHandler,
    addConnectHandler,
    addDisconnectHandler,
};
Object.defineProperty(exports, 'endpoint', {
    get: () => wsEndpoint,
})
Object.defineProperty(exports, 'isConnecting', {
    get: () => ws.readyState === ws.CONNECTING,
})
Object.defineProperty(exports, 'isReady', {
    get: () => ws.readyState === ws.OPEN,
})
Object.defineProperty(exports, 'isClosing', {
    get: () => ws.readyState === ws.CLOSING,
})
Object.defineProperty(exports, 'isClosed', {
    get: () => ws.readyState === ws.CLOSED,
})

doIfDev(() => {
    Object.defineProperty(exports, '_ws', {
        get: () => ws,
    })
});

export default exports;
